import { Injectable } from '@angular/core';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';

/** VAT report data and submission operations. Authentication and UI belong to callers. */
@Injectable({ providedIn: 'root' })
export class VatReportService {
    constructor(private datatask: DataTaskService) {}

    /**
     * Loads the report menu and its submission/receipt state.
     * @datatask 3134
     * @datataskName
     * @param params Report identifier.
     */
    public async getVatReportMenu(params: VatReportParams): Promise<VatReportMenuGet> {
        return (await this.datatask.Post(3134, params))[0];
    }

    /**
     * Recalculates the report from current accounting data.
     * @datatask 3135
     * @datataskName
     * @param params Report identifier.
     */
    public async updateReport(params: VatReportParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3135, params))[0];
    }

    /**
     * Accounts the report.
     * @datatask 3137
     * @datataskName
     * @param params Report identifier.
     */
    public async accountReport(params: VatReportParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3137, params))[0];
    }

    /**
     * Creates the XML export; filecontent uses URL-safe Base64 encoding.
     * @datatask 3141
     * @datataskName
     * @param params Report identifier.
     */
    public async exportReport(params: VatReportParams): Promise<VatReportExportResponse> {
        return (await this.datatask.Post(3141, params))[0];
    }

    /**
     * Deletes the report using the legacy full menu payload.
     * @datatask 3194
     * @datataskName
     * @param params Report menu record.
     */
    public async deleteReport(params: VatReportMenuGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3194, params))[0];
    }

    /**
     * Loads all submission steps, including the initial zero step.
     * @datatask 3259
     * @datataskName
     * @param params Empty procedure parameters.
     */
    public async getSubmissionSteps(params: Record<string, never> = {}): Promise<VatReportSubmissionStep[]> {
        return await this.datatask.Post(3259, params);
    }

    /**
     * Validates the report with the caller's ID-porten access token.
     * @datatask 3260
     * @datataskName
     * @param params Report identifier and access token.
     */
    public async validateReport(params: VatReportAuthenticatedParams): Promise<VatReportSubmissionResponse> {
        return (await this.datatask.Post(3260, params))[0];
    }

    /**
     * Sends the report, or only transfers it when transfer_only is 1.
     * @datatask 3271
     * @datataskName
     * @param params Report identifier, access token and transfer policy.
     */
    public async sendToAltinn(params: VatReportSendParams): Promise<VatReportSubmissionResponse> {
        return (await this.datatask.Post(3271, params))[0];
    }

    /**
     * Retrieves the submission status and PDF feedback receipt.
     * @datatask 3273
     * @datataskName
     * @param params Report identifier and access token.
     */
    public async retrieveFeedback(params: VatReportAuthenticatedParams): Promise<VatReportSubmissionResponse> {
        return (await this.datatask.Post(3273, params))[0];
    }
}

export interface VatReportParams {
    vatreport_keyno: string;
}

/** Database numbers arrive as strings; retain the existing API field names. */
export interface VatReportMenuGet extends VatReportParams {
    businessco_no: string;
    businessco_companyname: string;
    taxperiod_name: string;
    reg_datetime: string;
    reg_by_portal_user: string;
    last_updated_dt: string;
    acvoucherbunt_keyno: string;
    acvoucherbunt_keyno_rollback: string;
    vatreport_keyno_replaced_by: string;
    vatreport_keyno_replacment_for: string;
    is_accounted: '0' | '1';
    is_sent: '0' | '1';
    submission_step_no: string;
    receipt_base64?: string;
}

export interface VatReportSubmissionStep {
    step_no: string;
    step_name: string;
}

export interface VatReportAuthenticatedParams extends VatReportParams {
    id_porten_token: string;
}

export interface VatReportSendParams extends VatReportAuthenticatedParams {
    transfer_only: 0 | 1;
}

export interface VatReportSubmissionResponse extends ErrorMessageResponse {
    submission_step_no?: string;
    receipt_base64?: string;
}

export interface VatReportExportResponse extends ErrorMessageResponse {
    filecontent: string;
    filename: string;
}
